/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import { ArrayUtil, ObjectUtil } from '@cedalo/util';
import CheckIcon from '@mui/icons-material/esm/Check';
import VersionUpgrade from '@mui/icons-material/esm/Warning';
import {
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid as MUIGrid,
	InputAdornment,
	MenuItem,
	TextField,
	Tooltip,
	Typography
} from '@mui/material';
import React, { useEffect, useMemo, useReducer } from 'react';
import { FormattedMessage } from 'react-intl';
import { license as licenseUtils } from '../../utils';
import { withUser } from '../../UserProvider';
import { runQuery, useQuery } from '../app/GraphQLWSClient';
import DialogButton from '../utils/DialogButton';
import { Overlay } from '../utils/Overlay';
import { ConfirmImportDialog } from './ConfirmImportDialog';
import { ErrorDialog } from './ErrorDialog';

const UpgradeIcon = (props) => (
	<Tooltip enterDelay={300} title={props.tooltip}>
		<VersionUpgrade style={{ fontSize: '16px' }} color="error" />
	</Tooltip>
);

const IMPORT_INFO_QUERY = `
	query GetImportInfo($scope: ScopeInput!, $machinesImportInfo: [MachineImportInfoInput!]!, $initialQuery: Boolean!) {
		getImportInfo(input: {scope: $scope, machines: $machinesImportInfo}){
			machines {
				id
				nameInUse
			}
		}

		
		machines @include(if: $initialQuery) {
			name
			scope {
				id
			}
		}
	}
`;

const DO_IMPORT = `
	mutation DoImport($input: ImportInput!) {
		import(input: $input)	{
			success
			code
			info
		}
	}
`;

const updateAt = (state, [pathElement, ...rest]: any[], updater) =>
	pathElement
		? {
				...state,
				[pathElement]: updateAt(state[pathElement], rest, updater)
		  }
		: updater(state);

const reducer = (state, action) => {
	switch (action.type) {
		case 'init': {
			const { machines, usage } = action.data;
			const machineUsage = new Set(usage.machines.filter((i) => i.nameInUse).map((i) => i.id));
			const existingMachines = state.existingMachines || action.data.existingMachines;
			const existingInScope = existingMachines.filter(({ scope }) => scope.id === state.scope.id);
			return {
				...state,
				existingMachines: existingMachines || state.existingMachines,
				existingInScope,
				machines: !state.existingMachines
					? machines.reduce(
							(acc, { machine }) => ({
								...acc,
								[machine.id]: {
									id: machine.id,
									selected: true,
									name: machine.name,
									newName: machine.name,
									nameInUse: machineUsage.has(machine.id),
									error: null
								}
							}),
							{}
					  )
					: ObjectUtil.map(state.machines, ([id, info]) => [
							id,
							{ ...info, nameInUse: existingInScope.some(({ name }) => name === info.newName) }
					  ])
			};
		}
		// case 'set_scope':
		// 	return {
		// 		...state,
		// 		scope: {id, action.data.id},
		// 		existingInScope:
		// 	}
		case 'machine_toggle':
			return updateAt(state, ['machines', action.data], (old) => ({
				...old,
				selected: !old.selected
			}));
		case 'machine_set_name':
			return updateAt(state, ['machines', action.data.id], (old) => {
				return {
					...old,
					newName: action.data.name,
					nameInUse: state.existingInScope.some(({ name }) => name === action.data.name)
				};
			});
		case 'set_scope':
			return { ...state, scope: action.data };
		case 'show_confirmation':
			return {
				...state,
				showConfirmation: true
			};
		case 'cancel_confirmation':
			return {
				...state,
				showConfirmation: false
			};
		case 'start_import':
			return {
				...state,
				importing: true
			};
		case 'import_success':
			return {
				...state,
				importing: false,
				importDone: true
			};
		case 'import_error':
			return {
				...state,
				importing: false,
				importError: action.data
			};
		case 'import_error_done':
			return {
				...state,
				importDone: true
			};
		default:
			throw new Error(`UNKNOWN ACTION: ${action.type}`);
	}
};

const withShowable =
	(Component) =>
	({ show = undefined, ...props }) =>
		show !== false ? <Component {...props} /> : null;

const Grid = withShowable(MUIGrid);

const FormattedError = (props) => {
	const { error } = props;
	switch (error) {
		case 'MISSING_NAME':
			return <FormattedMessage id="Import.Error.NameRequired" defaultMessage="New name is missing!" />;
		case 'DUPLICATE_NAME':
			return (
				<FormattedMessage id="Import.Error.DuplicateName" defaultMessage="Duplicate name in current import!" />
			);
		default:
			return null;
	}
};

const MachineList = withShowable((props) => {
	const { machines, dispatch, machineErrors, showConnectorSelect } = props;

	const machineRows = Object.values(machines).map(({ selected, name, newName, id, nameInUse }) => {
		const error = selected && machineErrors[id];
		const inputProps =
			nameInUse && selected
				? {
						style: {
							width: 'calc(100% - 14px)'
						},
						endAdornment: (
							<InputAdornment position="end">
								<UpgradeIcon
									tooltip={
										<FormattedMessage
											id="Import.UpdateMachineIcon.Tooltip"
											defaultMessage="Name already in use. Existing Machine with same name will be replaced!"
										/>
									}
								/>
							</InputAdornment>
						)
				  }
				: null;

		return (
			<Grid item container sm={12} key={id} alignItems="center" spacing={1}>
				<Grid
					item
					container
					alignItems="center"
					justifyContent="flex-start"
					md={showConnectorSelect ? 3 : 5}
					sm={5}
					onClick={() => dispatch({ type: 'machine_toggle', data: id })}
					style={{ cursor: 'pointer' }}
				>
					<Grid item sm={3}>
						<Checkbox checked={selected} tabIndex={-1} disableRipple />
					</Grid>
					<Grid item sm={9}>
						<Tooltip enterDelay={300} title={name}>
							<Typography variant="subtitle1" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{name}
							</Typography>
						</Tooltip>
					</Grid>
				</Grid>

				<Grid item xs={12} sm={7} md={showConnectorSelect ? 5 : 7}>
					<TextField
						id={id}
						label={<FormattedMessage id="Import.Machine.ImportAs" defaultMessage="Import As" />}
						variant="outlined"
						error={!!error}
						helperText={error ? <FormattedError error={error} /> : undefined}
						disabled={!selected}
						value={newName}
						fullWidth
						onChange={(event) =>
							dispatch({
								type: 'machine_set_name',
								data: {
									id,
									name: event.target.value
								}
							})
						}
						InputProps={inputProps}
					/>
				</Grid>
			</Grid>
		);
	});

	return (
		<React.Fragment>
			<Grid item>
				<Typography style={{ padding: '16px 0px' }} variant="h6" color="textPrimary">
					<FormattedMessage id="Import.List.Machines.Title" defaultMessage="Machines" />
				</Typography>
			</Grid>

			<Grid item container spacing={1} alignItems="center">
				{ArrayUtil.intersperse(machineRows, (index) => (
					<Grid key={`sep-${index}`} item sm={12}>
						<Divider />
					</Grid>
				))}
			</Grid>
		</React.Fragment>
	);
});

// const validateFileVersions = (machines, selectedIds) => {
// 	let falsyMachine;
// 	const allValid = machines.every(({ machine }) => {
// 		if (selectedIds.includes(machine.id)) {
// 			const valid = machine.metadata.fileVersion === FILE_VERSION;
// 			if (!valid) falsyMachine = { version: machine.metadata.fileVersion, machine: machine.name };
// 			return valid;
// 		}
// 		return true;
// 	});
// 	return allValid
// 		? undefined
// 		: { code: 'FILE_VERSION_UNSUPPORTED', info: { requiredVersion: FILE_VERSION, ...falsyMachine } };
// };
const validateAvailableSheets = (machines, selectedIds, license = {}) => {
	const availableStreamsheets = licenseUtils.getAvailableSheetsCount(license);
	const requiredStreamsheets = machines.reduce((total, data) => {
		const { machine = {} } = data;
		const { id, streamsheets = [] } = machine;
		return selectedIds.includes(id) ? total + streamsheets.length : total;
	}, 0);
	return availableStreamsheets >= 0 && availableStreamsheets < requiredStreamsheets
		? { code: 'NOT_ENOUGH_STREAMSHEETS', info: { ...license, requiredStreamsheets } }
		: undefined;
};
const validateImport = (machines, selectedIds, license) => validateAvailableSheets(machines, selectedIds, license);
// validateFileVersions(machines, selectedIds) || validateAvailableSheets(machines, selectedIds, license);

const getErrorDialogInfo = (code, info: any = {}) => {
	if (code === 'NOT_ENOUGH_STREAMSHEETS') {
		const { maxStreamsheets = 0, usedStreamsheets = 0, requiredStreamsheets: required = 0 } = info;
		const remain = Math.max(0, maxStreamsheets - usedStreamsheets);
		return {
			titleId: 'Import.Error.NotPossible',
			messageId: 'Import.Error.Message.NotEnoughSheets',
			values: { required, remain }
		};
	}
	if (code === 'FILE_VERSION_UNSUPPORTED') {
		return {
			titleId: 'Import.Error.NotPossible',
			messageId: 'FileVersion.Error.Unsupported.Detail',
			values: info
		};
	}
	return undefined;
};
const handleInnerError = ({ code, info }: any = {}, dispatch) =>
	dispatch({ type: 'import_error', data: { code, errorInfo: getErrorDialogInfo(code, info) } });

export const ImportDialog = withUser(({ user, licenseInfo }) => ({ scopes: user.scopes, licenseInfo }))(
	({ importData, licenseInfo, onClose, scopes, initialScopeId }) => {
		// TODO: handle import with no machines
		const [state, dispatch] = useReducer(reducer, {
			machines: {},
			scope: { id: (scopes.find((s) => s.id === initialScopeId) || scopes[0]).id },
			showConfirmation: false
		});
		const machinesImportInfo = useMemo(
			() => importData.machines.map(({ machine }) => ({ id: machine.id, name: machine.name })),
			[importData]
		);
		const importInfoQuery = useQuery(IMPORT_INFO_QUERY, {
			scope: state.scope,
			machinesImportInfo,
			initialQuery: !state.existingMachines
		});
		useEffect(() => {
			if (importInfoQuery.status === 'done') {
				dispatch({
					type: 'init',
					data: {
						machines: importData.machines,
						usage: importInfoQuery.result.getImportInfo,
						existingMachines: importInfoQuery.result.machines
					}
				});
			} else if (importInfoQuery.status === 'error') {
				console.error(importInfoQuery.result);
			}
		}, [importInfoQuery]);

		useEffect(() => {
			if (state.importDone) {
				setTimeout(onClose, 500);
			}
		}, [state.importDone]);

		const doImport = async () => {
			dispatch({ type: 'start_import' });
			const machineSelection = Object.values<any>(state.machines)
				.filter((m) => m.selected)
				.map((m) => ({ id: m.id, newName: m.newName }));
			const selectedMachineIds = machineSelection.map((s) => s.id);
			const trimmedImportFileContent = {
				...importData,
				machines: importData.machines.filter((m) => selectedMachineIds.includes(m.machine.id))
			};
			try {
				const validationError = validateImport(importData.machines, selectedMachineIds, licenseInfo);
				if (!validationError) {
					const result = await runQuery(DO_IMPORT, {
						input: {
							scope: state.scope,
							file: trimmedImportFileContent,
							machines: machineSelection
						}
					});
					const { success } = result.import;
					if (success) {
						dispatch({ type: 'import_success' });
					} else {
						handleInnerError(result.data.import, dispatch);
					}
				} else {
					handleInnerError(validationError, dispatch);
				}
			} catch (error) {
				dispatch({ type: 'import_error', data: { code: 'UNEXPECTED_ERROR' } });
				console.error(error);
			}
		};

		const duplicateMachineNames = Object.fromEntries(
			Object.entries(ArrayUtil.partition<any>(Object.values(state.machines), (s) => s.newName)).filter(
				([, v]) => v.length > 1
			)
		);

		const machineErrors = Object.fromEntries(
			Object.entries<any>(state.machines).map(([k, v]) => [
				k,
				duplicateMachineNames[v.newName] ? 'DUPLICATE_NAME' : v.error
			])
		);

		const importAllowed = [...Object.values(machineErrors)].every((error) => error === null);

		const machinesThatOverwrite = Object.values<any>(state.machines).filter((m) => m.nameInUse && m.selected);

		if (state.showConfirmation) {
			return (
				<ConfirmImportDialog
					machines={machinesThatOverwrite}
					onCancel={() => dispatch({ type: 'cancel_confirmation' })}
					onConfirm={() => {
						doImport();
					}}
				>
					{state.importing && (
						<Overlay>
							<CircularProgress style={{ width: '24px', height: '24px' }} />
						</Overlay>
					)}
					{state.importDone && (
						<Overlay>
							<CheckIcon color="primary" />
						</Overlay>
					)}
				</ConfirmImportDialog>
			);
		}

		if (state.importError?.errorInfo) {
			return (
				<ErrorDialog
					open
					{...state.importError?.errorInfo}
					onClose={() => dispatch({ type: 'import_error_done' })}
				/>
			);
		}

		const content =
			importInfoQuery.status === 'loading' ? (
				<React.Fragment>
					<DialogContent style={{ margin: '16px auto' }}>
						<DialogContentText>
							<FormattedMessage id="Import.Loading" defaultMessage="Loading..." />
						</DialogContentText>
					</DialogContent>
					<Divider />
					<DialogActions>
						<DialogButton margin onClick={onClose}>
							<FormattedMessage id="Cancel" defaultMessage="Cancel" />
						</DialogButton>
					</DialogActions>
				</React.Fragment>
			) : (
				<React.Fragment>
					<DialogContent>
						{scopes.length > 1 ? (
							<TextField
								margin="normal"
								fullWidth
								select
								variant="outlined"
								size="small"
								value={state.scope.id}
								onChange={(event) => {
									dispatch({ type: 'set_scope', data: { id: event.target.value } });
								}}
								label={<FormattedMessage id="Extensions.Workspace" defaultMessage="Workspace" />}
							>
								{scopes
									.filter(({ rights }) => rights.includes('machine.edit'))
									.map((s) => (
										<MenuItem key={s.id} value={s.id}>
											{s.name}
										</MenuItem>
									))}
							</TextField>
						) : null}
						<Grid container direction="column">
							<MachineList
								machines={state.machines}
								showConnectorSelect={state.showConnectorSelect}
								machineErrors={machineErrors}
								dispatch={dispatch}
							/>
						</Grid>
					</DialogContent>
					<Divider />
					<DialogActions>
						{/* {!isImporting && !importDone ? ( */}
						<React.Fragment>
							<DialogButton margin onClick={onClose}>
								<FormattedMessage id="Cancel" defaultMessage="Cancel" />
							</DialogButton>
							<DialogButton
								margin
								disabled={!importAllowed}
								onClick={() => {
									const hasOverwrites = machinesThatOverwrite.length > 0;
									if (hasOverwrites) {
										dispatch({ type: 'show_confirmation' });
									} else {
										doImport();
									}
								}}
							>
								<FormattedMessage id="Import.Button.Import" defaultMessage="Import" />
							</DialogButton>
						</React.Fragment>
						{/* ) : null} */}
						{/* {importDone ? (
					<Button onClick={props.closeImportDialog}>
						<FormattedMessage id="Close" defaultMessage="Close" />
						</Button>
				) : null} */}
					</DialogActions>
					{state.importing && (
						<Overlay>
							<CircularProgress style={{ width: '24px', height: '24px' }} />
						</Overlay>
					)}
					{state.importDone && (
						<Overlay>
							<CheckIcon color="primary" />
						</Overlay>
					)}
				</React.Fragment>
			);

		return (
			<Dialog fullWidth open maxWidth="sm" onClose={onClose} key="dialog">
				<DialogTitle>
					<FormattedMessage id="Import.Dialog.Title" defaultMessage="Import" />
				</DialogTitle>
				{content}
			</Dialog>
		);
	}
);
